// partners/pages/PartnerApplicationForm.jsx
// React + Tailwind — MNF Partner Application Form
// FIX #1 applied: partner_type uses underscore ('creative_ambassador')
// FIX #2 applied: Real fetch() POST to /api/partners/applications/
// FIX #12 note: CSRF token should be handled by your axios/fetch interceptor

import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  useSubmitPartnerApplicationMutation,
  useLazyCheckPartnerApplicationEmailQuery,
} from "../../../apis/apiSlice";
import { capturePartnerEvent } from "../../../utils/analytics";

const FIELD_CLASS ="w-full px-4 py-3 bg-mnf-dark-3 border border-white/[0.08] text-mnf-text-primary rounded-lg font-inter text-[15px] transition-colors focus:border-mnf-cyan focus:outline-none placeholder:text-mnf-text-muted";
const LABEL_CLASS = "block font-poppins text-[13px] font-semibold text-mnf-text-secondary mb-1.5";

// Must match PartnerApplication.BusinessCategory / Platform in partners/models.py
const BUSINESS_CATEGORIES = [
  { value: "production_house", label: "Production House" },
  { value: "casting_agency", label: "Casting Agency" },
  { value: "equipment_rental", label: "Film Equipment Rental" },
  { value: "acting_school", label: "Acting / Film School" },
  { value: "editing_studio", label: "Editing / Post-Production Studio" },
  { value: "festival_organizer", label: "Film Festival Organizer" },
  { value: "filmmaking_collective", label: "Filmmaking Collective / Community" },
  { value: "talent_management", label: "Talent Management Agency" },
  { value: "other", label: "Other" },
];

const PLATFORMS = [
  { value: "youtube", label: "YouTube" },
  { value: "instagram", label: "Instagram" },
  { value: "blog", label: "Blog / Website" },
  { value: "twitter", label: "Twitter / X" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "podcast", label: "Podcast" },
  { value: "other", label: "Other" },
];

function ApplicationToast({ message, onClose }) {
  if (!message) return null;
  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm animate-fade-up">
      <div className="flex items-start gap-3 px-4 py-3.5 rounded-lg bg-mnf-dark-2 border border-mnf-error/30 shadow-[0_12px_40px_rgba(0,0,0,0.45)]">
        <span className="text-mnf-error text-lg leading-none shrink-0">!</span>
        <p className="text-sm text-mnf-text-primary leading-relaxed flex-1">{message}</p>
        <button
          type="button"
          onClick={onClose}
          className="text-mnf-text-muted hover:text-mnf-text-primary text-lg leading-none shrink-0"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default function PartnerApplicationForm() {
  const { type } = useParams();
  const [tab, setTab] = useState(
    type === "creative_ambassador" ? "creative_ambassador" : "ecosystem"
  );
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [submitApplication, { isLoading: submitting }] = useSubmitPartnerApplicationMutation();
  const [checkApplicationEmail] = useLazyCheckPartnerApplicationEmailQuery();

  useEffect(() => {
    capturePartnerEvent("partner_application_viewed", { partner_type: tab });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once on mount
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 6000);
    return () => clearTimeout(timer);
  }, [toast]);

  const showEmailToast = useCallback((message) => {
    setToast(message);
    setError(message);
  }, []);

  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", city: "",
    business_name: "", business_category: "", website: "", years_operating: "", community_size: "", interaction_method: "",
    channel_name: "", platform: "", channel_url: "", follower_count: "", content_niche: "", avg_engagement: "",
    motivation: "", expected_referrals: "", used_mnf: "", additional_notes: "",
    payment_method: "", payment_details: "", pan_number: "",
    agreed_terms: false, opted_in_comms: false,
  });

  const set = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setToast("");

    const payload = { ...form, partner_type: tab, email: form.email.trim() };

    try {
      const emailCheck = await checkApplicationEmail(payload.email).unwrap();
      if (!emailCheck?.available) {
        capturePartnerEvent("partner_application_email_blocked", {
          partner_type: tab,
          reason: emailCheck?.message,
        });
        showEmailToast(
          emailCheck?.message || "This email cannot be used for a partner application."
        );
        return;
      }
    } catch (err) {
      const checkMsg =
        err?.data?.message ||
        (Array.isArray(err?.data?.email) ? err.data.email[0] : null);
      if (checkMsg) {
        capturePartnerEvent("partner_application_email_check_error", { partner_type: tab });
        showEmailToast(checkMsg);
        return;
      }
    }

    try {
      await submitApplication(payload).unwrap();
      setSubmitted(true);
      capturePartnerEvent("partner_application_submitted", {
        partner_type: tab,
        business_category: form.business_category || undefined,
        platform: form.platform || undefined,
        opted_in_comms: form.opted_in_comms,
      });
    } catch (err) {
      if (err?.data && typeof err.data === 'object') {
        const firstField = Object.keys(err.data)[0];
        const firstMsg = Array.isArray(err.data[firstField])
          ? err.data[firstField][0]
          : err.data[firstField];
        const message = firstMsg || "Submission failed. Please try again.";
        capturePartnerEvent("partner_application_submit_failed", {
          partner_type: tab,
          error_field: firstField || "network",
        });
        if (firstField === "email") {
          showEmailToast(message);
        } else {
          setError(message);
        }
      } else {
        capturePartnerEvent("partner_application_submit_failed", {
          partner_type: tab,
          error_field: "network",
        });
        setError("Network error. Please check your connection and try again.");
      }
    }
  };

  if (submitted) {
    return (
      <div className="bg-mnf-dark-1 min-h-screen font-inter text-mnf-text-primary flex items-center justify-center px-6">
        <div className="text-center animate-fade-up max-w-md">
          <div className="w-20 h-20 rounded-full bg-mnf-success/10 border-2 border-mnf-success flex items-center justify-center text-4xl mx-auto mb-6">✓</div>
          <h2 className="font-poppins text-[28px] font-bold mb-3">Application Received!</h2>
          <p className="text-mnf-text-secondary text-base mb-2">We'll review your application within <strong className="text-mnf-text-primary">48 hours</strong> and reach out via email.</p>
          <p className="text-sm text-mnf-text-muted mt-4">In the meantime, try MNF for free — <a href="https://app.mnf.ai/personal-registration" className="text-mnf-cyan hover:underline">Sign up here</a></p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-mnf-dark-1 min-h-screen font-inter text-mnf-text-primary">
      <div className="max-w-[680px] mx-auto px-6 pt-16 pb-24">
        <a href="/become-a-partner" className="inline-flex items-center gap-1.5 text-mnf-text-muted text-sm mb-8 hover:text-mnf-cyan transition-colors">← Back to Partner Program</a>
        <h1 className="font-poppins text-[32px] font-bold mb-2">Partner Application</h1>
        <p className={`text-base text-mnf-text-secondary ${type === "ecosystem" ? "mb-2" : "mb-8"}`}>
          Takes about 3 minutes. We review every application within 48 hours.
        </p>
        {type === "ecosystem" && (
          <p className="text-sm text-mnf-text-muted mb-8 leading-relaxed">
            Please use a different email address than your MNF user account login email for this application.
          </p>
        )}

        {/* Tabs */}
        <div className="flex mb-10 rounded-mnf overflow-hidden border border-white/10">
          {[
            { key: "ecosystem", label: "🎬 Film Ecosystem Partner" },
            { key: "creative_ambassador", label: "📹 Creative Ambassador" },
          ].map((t) => (
            <button key={t.key} onClick={() => { setTab(t.key); capturePartnerEvent("partner_application_tab_switched", { partner_type: t.key }); }} className={`flex-1 py-3.5 text-center font-poppins text-sm font-semibold transition-all ${tab === t.key ? "bg-mnf-gradient text-mnf-btn-text" : "bg-mnf-dark-3 text-mnf-text-secondary hover:bg-mnf-dark-4 hover:text-mnf-text-primary"}`}>
              {t.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit}>
          {/* Contact Info */}
          <fieldset className="mb-8">
            <legend className="font-poppins text-lg font-semibold mb-4 pb-2 border-b border-white/[0.08] w-full">Contact Information</legend>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={LABEL_CLASS}>Full Name <span className="text-mnf-error">*</span></label><input type="text" required value={form.full_name} onChange={set("full_name")} className={FIELD_CLASS} /></div>
              <div><label className={LABEL_CLASS}>Email <span className="text-mnf-error">*</span></label><input type="email" required value={form.email} onChange={set("email")} className={FIELD_CLASS} /></div>
              <div><label className={LABEL_CLASS}>Phone <span className="text-mnf-error">*</span></label><input type="tel" required value={form.phone} onChange={set("phone")} className={FIELD_CLASS} /></div>
              <div><label className={LABEL_CLASS}>City <span className="text-mnf-error">*</span></label><input type="text" required value={form.city} onChange={set("city")} className={FIELD_CLASS} /></div>
            </div>
          </fieldset>

          {/* Ecosystem-specific */}
          {tab === "ecosystem" && (
            <fieldset className="mb-8">
              <legend className="font-poppins text-lg font-semibold mb-4 pb-2 border-b border-white/[0.08] w-full">Business Information</legend>
              <div className="space-y-4">
                <div><label className={LABEL_CLASS}>Business / Organization Name <span className="text-mnf-error">*</span></label><input type="text" required value={form.business_name} onChange={set("business_name")} className={FIELD_CLASS} /></div>
                <div><label className={LABEL_CLASS}>Business Category <span className="text-mnf-error">*</span></label>
                  <select required value={form.business_category} onChange={set("business_category")} className={FIELD_CLASS}>
                    <option value="">Select category...</option>
                    {BUSINESS_CATEGORIES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div><label className={LABEL_CLASS}>Website or Social Link</label><input type="url" value={form.website} onChange={set("website")} placeholder="https://" className={FIELD_CLASS} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className={LABEL_CLASS}>Years in Operation <span className="text-mnf-error">*</span></label><select required value={form.years_operating} onChange={set("years_operating")} className={FIELD_CLASS}><option value="">Select...</option>{["Less than 1 year", "1-3 years", "3-5 years", "5-10 years", "10+ years"].map((o) => <option key={o}>{o}</option>)}</select></div>
                  <div><label className={LABEL_CLASS}>Community Size <span className="text-mnf-error">*</span></label><select required value={form.community_size} onChange={set("community_size")} className={FIELD_CLASS}><option value="">Select...</option>{["Under 100", "100-500", "500-2,000", "2,000-10,000", "10,000+"].map((o) => <option key={o}>{o}</option>)}</select></div>
                </div>
                <div><label className={LABEL_CLASS}>How do you interact with filmmakers?</label><textarea value={form.interaction_method} onChange={set("interaction_method")} placeholder="e.g., In-person, events, social media groups, email..." className={`${FIELD_CLASS} min-h-[100px] resize-y`} /></div>
              </div>
            </fieldset>
          )}

          {/* Ambassador-specific */}
          {tab === "creative_ambassador" && (
            <fieldset className="mb-8">
              <legend className="font-poppins text-lg font-semibold mb-4 pb-2 border-b border-white/[0.08] w-full">Channel Information</legend>
              <div className="space-y-4">
                <div><label className={LABEL_CLASS}>Primary Channel / Handle <span className="text-mnf-error">*</span></label><input type="text" required value={form.channel_name} onChange={set("channel_name")} placeholder="@yourhandle" className={FIELD_CLASS} /></div>
                <div><label className={LABEL_CLASS}>Primary Platform <span className="text-mnf-error">*</span></label><select required value={form.platform} onChange={set("platform")} className={FIELD_CLASS}><option value="">Select...</option>{PLATFORMS.map(({ value, label }) => (<option key={value} value={value}>{label}</option>))}</select></div>
                <div><label className={LABEL_CLASS}>Channel URL <span className="text-mnf-error">*</span></label><input type="url" required value={form.channel_url} onChange={set("channel_url")} placeholder="https://" className={FIELD_CLASS} /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className={LABEL_CLASS}>Followers / Subscribers <span className="text-mnf-error">*</span></label><select required value={form.follower_count} onChange={set("follower_count")} className={FIELD_CLASS}><option value="">Select...</option>{["1,000-5,000", "5,000-25,000", "25,000-100,000", "100,000-500,000", "500,000+"].map((o) => <option key={o}>{o}</option>)}</select></div>
                  <div><label className={LABEL_CLASS}>Content Niche <span className="text-mnf-error">*</span></label><select required value={form.content_niche} onChange={set("content_niche")} className={FIELD_CLASS}><option value="">Select...</option>{["Filmmaking / Cinematography", "Screenwriting / Storytelling", "Film Reviews / Analysis", "Creative Tools / Tech", "Acting / Performance", "Film Education", "Other Creative"].map((o) => <option key={o}>{o}</option>)}</select></div>
                </div>
                <div><label className={LABEL_CLASS}>Average Views / Engagement</label><input type="text" value={form.avg_engagement} onChange={set("avg_engagement")} placeholder="e.g., 5,000 views, 200 likes" className={FIELD_CLASS} /></div>
              </div>
            </fieldset>
          )}

          {/* Partnership Details */}
          <fieldset className="mb-8">
            <legend className="font-poppins text-lg font-semibold mb-4 pb-2 border-b border-white/[0.08] w-full">Partnership Details</legend>
            <div className="space-y-4">
              <div><label className={LABEL_CLASS}>Why do you want to partner with MNF? <span className="text-mnf-error">*</span></label><textarea required value={form.motivation} onChange={set("motivation")} placeholder="Tell us about your interest..." className={`${FIELD_CLASS} min-h-[100px] resize-y`} /></div>
              <div><label className={LABEL_CLASS}>Expected monthly referrals</label><select value={form.expected_referrals} onChange={set("expected_referrals")} className={FIELD_CLASS}><option value="">Select...</option>{["1-10", "10-25", "25-50", "50-100", "100+"].map((o) => <option key={o}>{o}</option>)}</select></div>
              <div><label className={LABEL_CLASS}>Have you used MNF before? <span className="text-mnf-error">*</span></label><select required value={form.used_mnf} onChange={set("used_mnf")} className={FIELD_CLASS}><option value="">Select...</option><option value="yes_active">Yes, I'm an active user</option><option value="yes_tried">Yes, I've tried it</option><option value="no">No, but I'd like to</option></select></div>
              <div><label className={LABEL_CLASS}>Anything else?</label><textarea value={form.additional_notes} onChange={set("additional_notes")} placeholder="Optional" className={`${FIELD_CLASS} min-h-[80px] resize-y`} /></div>
            </div>
          </fieldset>

          {/* Payment */}
          <fieldset className="mb-8">
            <legend className="font-poppins text-lg font-semibold mb-4 pb-2 border-b border-white/[0.08] w-full">Payment Information</legend>
            <div className="space-y-4">
              <div><label className={LABEL_CLASS}>Preferred Payment Method <span className="text-mnf-error">*</span></label><select required value={form.payment_method} onChange={set("payment_method")} className={FIELD_CLASS}><option value="">Select...</option><option>UPI</option><option>Bank Transfer (NEFT/IMPS)</option><option>PayPal</option></select></div>
              <div><label className={LABEL_CLASS}>UPI ID / Bank Details</label><input type="text" value={form.payment_details} onChange={set("payment_details")} placeholder="Can provide after approval" className={FIELD_CLASS} /><p className="text-xs text-mnf-text-muted mt-1">Can be provided after approval</p></div>
              <div><label className={LABEL_CLASS}>PAN Number</label><input type="text" value={form.pan_number} onChange={set("pan_number")} placeholder="ABCDE1234F" className={FIELD_CLASS} /><p className="text-xs text-mnf-text-muted mt-1">Required for payouts above ₹15,000/year (TDS compliance). Can provide later.</p></div>
            </div>
          </fieldset>

          {/* Terms */}
          <div className="mb-8 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" required checked={form.agreed_terms} onChange={set("agreed_terms")} className="w-[18px] h-[18px] accent-[#00C3FF] mt-0.5 shrink-0" />
              <span className="text-[13px] text-mnf-text-secondary">I agree to the <a href="/partners/terms" target="_blank" rel="noreferrer" className="text-mnf-cyan hover:underline">Partner Program Terms</a> and <a href="https://mnf.ai/privacy" target="_blank" rel="noreferrer" className="text-mnf-cyan hover:underline">Privacy Policy</a> <span className="text-mnf-error">*</span></span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={form.opted_in_comms} onChange={set("opted_in_comms")} className="w-[18px] h-[18px] accent-[#00C3FF] mt-0.5 shrink-0" />
              <span className="text-[13px] text-mnf-text-secondary">I'd like to receive partner tips, product updates, and promotional resources</span>
            </label>
          </div>

          {error && <div className="mb-4 p-3 bg-mnf-error/10 border border-mnf-error/20 rounded-lg text-sm text-mnf-error">{error}</div>}

          <button
            type="submit"
            disabled={submitting || !form.agreed_terms}
            className="w-full py-4 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text font-poppins text-base font-bold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(0,195,255,0.25)] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none"
          >
            {submitting ? "Submitting..." : "Submit Application"}
          </button>
        </form>
        <ApplicationToast message={toast} onClose={() => setToast("")} />
      </div>
    </div>
  );
}
